import {
  bankAccountSchema,
  MAX_ORDER_REASON_LENGTH,
  type RequestOrderCancelInput,
  requestOrderCancelInputSchema,
} from '@repo/api-contracts';
import { type FormEvent, type ReactElement, useId, useRef, useState } from 'react';
import { isOrderConflict, orderMutationMessage, useModalDialog } from '@/shared/lib';
import { Button, cn, DialogFrame, FormField, Label } from '@/shared/ui';

interface OrderCancelDialogProps {
  readonly orderId: string;
  readonly requiresRefundAccount?: boolean;
  readonly onSubmit: (input: RequestOrderCancelInput) => Promise<void>;
  readonly onReload: () => void;
  readonly onClose: () => void;
  readonly pending?: boolean;
  readonly error?: string | null;
}

/**
 * 결제 완료 주문의 취소 요청(주문 설계 §8-2) — 사유(1~500자)를 받아 보낸다. 실패하면 입력을 지우지 않고 안내하고,
 * 상태 충돌이면 상세를 다시 읽는다. 처리 중에는 Escape 로 닫지 않는다. 모양은 공용 대화상자 틀(DialogFrame)이다.
 */
export const OrderCancelDialog = ({
  orderId,
  requiresRefundAccount = false,
  onSubmit,
  onReload,
  onClose,
  pending = false,
  error,
}: OrderCancelDialogProps): ReactElement => {
  const [refundAccount, setRefundAccount] = useState({ bank: '', account: '', holder: '' });
  const [reason, setReason] = useState('');
  /** 입력값 검증 오류 — 이것만 입력란을 잘못된 값(aria-invalid·빨간 테두리)으로 표시한다. */
  const [fieldError, setFieldError] = useState<string | null>(null);
  /** 요청 실패(통신·상태 충돌 등) 안내 — 입력값 탓이 아니므로 입력란은 그대로 둔다. */
  const [failure, setFailure] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const lock = useRef(false);
  const modal = useModalDialog(onClose, !(pending || saving));
  const reasonId = useId();
  const noticeId = `${reasonId}-notice`;
  const errorId = `${reasonId}-error`;
  const shown = fieldError ?? failure ?? error ?? null;

  const submit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    if (pending || lock.current) return;
    if (requiresRefundAccount && !bankAccountSchema.safeParse(refundAccount).success) {
      setFieldError('환불받을 계좌를 입력해 주세요');
      return;
    }
    const parsed = requestOrderCancelInputSchema.safeParse({
      orderId,
      reason,
      ...(requiresRefundAccount ? { refundAccount } : {}),
    });
    if (!parsed.success) {
      setFailure(null);
      setFieldError('취소 사유를 1~500자로 입력해 주세요.');
      return;
    }
    lock.current = true;
    setSaving(true);
    setFieldError(null);
    setFailure(null);
    try {
      // 성공 뒤 상세는 쓰기 훅이 응답(최신 상세)으로 갱신한다 — 같은 상세를 다시 읽지 않는다.
      await onSubmit(parsed.data);
      onClose();
    } catch (requestFailure) {
      setFailure(orderMutationMessage(requestFailure));
      if (isOrderConflict(requestFailure)) onReload();
    } finally {
      lock.current = false;
      setSaving(false);
    }
  };

  return (
    <DialogFrame
      modal={modal}
      title="취소 요청"
      onSubmit={(event) => {
        void submit(event);
      }}
      footer={
        <>
          <Button type="submit" variant="destructive" isLoading={saving} disabled={pending}>
            요청 제출
          </Button>
          <Button type="button" variant="ghost" disabled={pending || saving} onClick={onClose}>
            닫기
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <p id={noticeId}>취소 요청 후 환불 처리 결과를 확인해 주세요.</p>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={reasonId} isRequired>
            취소 사유
          </Label>
          <textarea
            id={reasonId}
            rows={4}
            value={reason}
            maxLength={MAX_ORDER_REASON_LENGTH}
            disabled={pending || saving}
            aria-invalid={fieldError ? true : undefined}
            aria-describedby={shown ? `${noticeId} ${errorId}` : noticeId}
            className={cn(
              'w-full rounded-2xl border border-input bg-background px-4 py-2 text-sm transition-colors',
              'placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              fieldError && 'border-destructive focus-visible:ring-destructive',
            )}
            placeholder="예: 행사 일정이 바뀌었습니다."
            onChange={(event) => setReason(event.target.value)}
          />
        </div>
        {requiresRefundAccount ? (
          <fieldset disabled={pending || saving} className="flex flex-col gap-3">
            <legend>환불받을 계좌</legend>
            {(
              [
                { key: 'bank', label: '환불 은행' },
                { key: 'account', label: '환불 계좌번호' },
                { key: 'holder', label: '환불 예금주' },
              ] as const
            ).map(({ key, label }) => (
              <FormField
                key={key}
                label={label}
                isRequired
                value={refundAccount[key]}
                onChange={(event) =>
                  setRefundAccount((previous) => ({ ...previous, [key]: event.target.value }))
                }
              />
            ))}
          </fieldset>
        ) : null}
        {shown ? (
          <p id={errorId} role="alert" className="text-destructive">
            {shown}
          </p>
        ) : null}
      </div>
    </DialogFrame>
  );
};
